"use strict";
/**
 * A small damped Newton solver for square systems of a few unknowns, with a
 * finite-difference Jacobian and box bounds. Aiming has 2 or 3 unknowns, so
 * the linear algebra is done in place on tiny arrays.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.solve = solve;
const norm = (r) => Math.hypot(...r);
/** A step that leaves more than this fraction of the residual is slow... */
const STALL_RATIO = 0.5;
/** ...and this many slow steps in a row is a stall. */
const STALL_STEPS = 3;
const clampInto = (u, lower, upper) => {
    for (let i = 0; i < u.length; i += 1) {
        u[i] = Math.min(upper[i], Math.max(lower[i], u[i]));
    }
};
/** Solves J x = b in place by Gaussian elimination with partial pivoting. */
function solveLinear(jacobian, b) {
    const n = b.length;
    const a = jacobian.map((row, i) => Float64Array.of(...row, b[i]));
    for (let col = 0; col < n; col += 1) {
        let pivot = col;
        for (let row = col + 1; row < n; row += 1) {
            if (Math.abs(a[row][col]) > Math.abs(a[pivot][col]))
                pivot = row;
        }
        if (Math.abs(a[pivot][col]) < 1e-14)
            return null;
        [a[col], a[pivot]] = [a[pivot], a[col]];
        for (let row = col + 1; row < n; row += 1) {
            const f = a[row][col] / a[col][col];
            for (let k = col; k <= n; k += 1)
                a[row][k] -= f * a[col][k];
        }
    }
    const x = new Float64Array(n);
    for (let row = n - 1; row >= 0; row -= 1) {
        let s = a[row][n];
        for (let k = row + 1; k < n; k += 1)
            s -= a[row][k] * x[k];
        x[row] = s / a[row][row];
    }
    return x;
}
/**
 * Damped quasi-Newton from u0. The Jacobian starts as a forward difference
 * (n extra evaluations) and is then kept current with Broyden's rank-one
 * update from each accepted step; it is rebuilt by differences only when a
 * step fails to reduce the residual. Steps are clipped to the bounds and
 * halved until the residual shrinks.
 */
function solve(residual, u0, options) {
    const n = u0.length;
    const u = Float64Array.from(u0);
    clampInto(u, options.lower, options.upper);
    const r = new Float64Array(n);
    if (!residual(u, r)) {
        return { u, r, converged: false, iterations: 0, defined: false, pinned: false };
    }
    const probe = new Float64Array(n);
    const probeR = new Float64Array(n);
    const trial = new Float64Array(n);
    const trialR = new Float64Array(n);
    const result = (converged, iterations) => ({
        u,
        r,
        converged,
        iterations,
        defined: true,
        pinned: u.some((v, i) => v === options.lower[i] || v === options.upper[i]),
    });
    /** Forward differences: column j = dr/du_j, stepping away from a bound. */
    const differenced = () => {
        const jacobian = Array.from({ length: n }, () => new Float64Array(n));
        for (let j = 0; j < n; j += 1) {
            probe.set(u);
            const h = u[j] + options.steps[j] > options.upper[j] ? -options.steps[j] : options.steps[j];
            probe[j] += h;
            if (!residual(probe, probeR))
                return null;
            for (let i = 0; i < n; i += 1)
                jacobian[i][j] = (probeR[i] - r[i]) / h;
        }
        return jacobian;
    };
    /** A step along the Newton direction that reduces the residual, applied to (u, r). */
    const descend = (jacobian) => {
        const step = solveLinear(jacobian, r.map((v) => -v));
        if (step === null)
            return false;
        for (let scale = 1; scale > 1 / 64; scale /= 2) {
            for (let i = 0; i < n; i += 1)
                trial[i] = u[i] + scale * step[i];
            clampInto(trial, options.lower, options.upper);
            if (residual(trial, trialR) && norm(trialR) < norm(r)) {
                broydenUpdate(jacobian, trial, trialR);
                u.set(trial);
                r.set(trialR);
                return true;
            }
        }
        return false;
    };
    /** J += (dr - J du) du^T / (du . du), for the step u -> next. */
    const broydenUpdate = (jacobian, next, nextR) => {
        let duDotDu = 0;
        for (let j = 0; j < n; j += 1)
            duDotDu += (next[j] - u[j]) ** 2;
        if (duDotDu === 0)
            return;
        for (let i = 0; i < n; i += 1) {
            let predicted = 0;
            for (let j = 0; j < n; j += 1)
                predicted += jacobian[i][j] * (next[j] - u[j]);
            const surprise = (nextR[i] - r[i] - predicted) / duDotDu;
            for (let j = 0; j < n; j += 1)
                jacobian[i][j] += surprise * (next[j] - u[j]);
        }
    };
    let jacobian = options.jacobian?.map((row) => Float64Array.from(row)) ?? null;
    let slowSteps = 0;
    for (let iteration = 1; iteration <= options.maxIterations; iteration += 1) {
        if (options.done(r))
            return result(true, iteration - 1);
        const before = norm(r);
        if (jacobian === null || !descend(jacobian)) {
            // no Jacobian yet, or the updated one led nowhere: difference afresh
            jacobian = differenced();
            if (jacobian === null || !descend(jacobian))
                return result(false, iteration);
        }
        // Near a root, Newton shrinks the residual many-fold per step. Steps
        // that barely shrink it mean a minimum that isn't a root (the target
        // is out of reach): stop rather than creep.
        slowSteps = norm(r) > STALL_RATIO * before ? slowSteps + 1 : 0;
        if (slowSteps === STALL_STEPS)
            return result(false, iteration);
    }
    return result(options.done(r), options.maxIterations);
}
